import "react-international-phone/style.css";

import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Drawer,
  Group,
  Image,
  Input,
  InputBase,
  Loader,
  Paper,
  Radio,
  RadioCard,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import {
  useDebouncedValue,
  useDisclosure,
  useMediaQuery,
} from "@mantine/hooks";
import { Spotlight, spotlight, SpotlightActionData } from "@mantine/spotlight";
import { HeadersArgs, LoaderFunction, MetaFunction } from "@remix-run/node";
import {
  json,
  Link,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useSearchParams,
} from "@remix-run/react";
import {
  IconAdjustmentsHorizontal,
  IconExclamationCircle,
  IconSearch,
} from "@tabler/icons-react";
import { useEffect, useMemo, useRef,useState } from "react";

import placeholderPerson from "~/assets/misc/placeholder-person.png";
import { BookHealthCareHero } from "~/components/book-healthcare-hero";
import { BusinessReviewBadge } from "~/components/business-review-badge";
import { ConsultationServiceFilter } from "~/components/consultation-service-filter";
import { getProfileFilterOptions } from "~/services/get-profile-filter-options";
import { DoctorCard } from "~/components/doctor-card";
import { DoctorSuggestionModal } from "~/components/doctor-suggestion-modal";
import {
  DEFAULT_OG_IMAGE_EN,
  DEFAULT_OG_IMAGE_FR,
  PAGE_TITLE,
} from "~/constants";
import { getCountryValues,getDoctorAvailabilityValues } from "~/data/dropdown-values";
import { specialtyLocationPages } from "~/data/specialty-location-pages";
import { TranslationKey, useTranslation } from "~/hooks/use-typed-translation";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import { getConsultationServices } from "~/services/get-consultation-services";
import { getDoctorProfiles } from "~/services/get-doctor-profiles";
import utilityClasses from "~/styles/utilities.module.scss";
import { ComboboxItemPrice, DoctorProfile, OkDataError } from "~/types";
import { resolveCurrencyContext } from "~/utils/currency.server";
import { getCentralizedMeta } from "~/utils/meta-helper";
import { renderLoadingErrorData } from "~/utils/render-loading-error-data";

import classes from "./book-doctor.module.scss";

const homepageSeoCopy = {
  fr: {
    title: `Médecins en ligne en Afrique francophone • ${PAGE_TITLE}`,
    description:
      "Trouvez des médecins et spécialistes francophones en Afrique et dans la diaspora pour une téléconsultation, une consultation à domicile ou un rendez-vous médical avec La Ruche Health.",
    keywords:
      "médecin en ligne Afrique, téléconsultation Afrique francophone, rendez-vous médecin Afrique, spécialiste en ligne, médecin francophone",
  },
  en: {
    title: `Online Doctors in Francophone Africa • ${PAGE_TITLE}`,
    description:
      "Find French-speaking doctors and specialists across Africa and the diaspora for teleconsultation, home care, or medical appointments with La Ruche Health.",
    keywords:
      "online doctor Africa, Francophone Africa teleconsultation, book doctor Africa, French-speaking doctors, online specialist Africa",
  },
};

const getHomepageStructuredData = ({
  origin,
  locale,
}: {
  origin: string;
  locale: "fr" | "en";
}) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalOrganization",
      "@id": `${origin}/${locale}#organization`,
      name: "La Ruche Health",
      url: `${origin}/${locale}`,
      areaServed: [
        {
          "@type": "Place",
          name:
            locale === "fr"
              ? "Afrique francophone"
              : "Francophone Africa",
        },
        {
          "@type": "Place",
          name:
            locale === "fr"
              ? "Diaspora africaine francophone"
              : "Francophone African diaspora",
        },
        { "@type": "Country", name: "Côte d'Ivoire" },
        { "@type": "Country", name: "Senegal" },
        { "@type": "Country", name: "Cameroon" },
        { "@type": "Country", name: "Benin" },
        { "@type": "Country", name: "Togo" },
        { "@type": "Country", name: "Mali" },
      ],
      medicalSpecialty: [
        "PrimaryCare",
        "Dermatology",
        "Gynecologic",
        "Pediatric",
        "Psychiatric",
        "Oncologic",
      ],
      availableService: [
        {
          "@type": "MedicalService",
          name:
            locale === "fr"
              ? "Téléconsultation médicale"
              : "Medical teleconsultation",
        },
        {
          "@type": "MedicalService",
          name:
            locale === "fr"
              ? "Consultation médicale à domicile"
              : "Home medical consultation",
        },
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${origin}/${locale}#website`,
      name: "La Ruche Health",
      url: `${origin}/${locale}`,
      inLanguage: locale,
      publisher: {
        "@id": `${origin}/${locale}#organization`,
      },
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/${locale}?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  ],
});

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];
  
  return getCentralizedMeta({
    title: data.title,
    description: data.description,
    image: data.locale === "fr" ? DEFAULT_OG_IMAGE_FR : DEFAULT_OG_IMAGE_EN,
    url: data.origin + location.pathname,
    locale: data.locale,
    keywords: data.keywords,
    alternateLanguages: {
      en: `${data.origin}/en`,
      fr: `${data.origin}/fr`,
    }
  });
};

export const headers = ({ loaderHeaders }: HeadersArgs) => {
  return loaderHeaders;
};

export default function BookAppointment() {
  const theme = useMantineTheme();
  const { t, i18n } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const isSmall = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`);
  const isLarge1 = useMediaQuery(`(min-width: ${theme.breakpoints.lg1})`);
  const [searchParams, setSearchParams] = useSearchParams();
  // console.log(searchParams.get("q"));

  const loaderData = useLoaderData<typeof loader>();
  const { data, error, ok, country, currency, doctorSuggestionsEnabled } = loaderData;
  // console.log("loader data:", data, "ok:", ok, "error:", error, "country:", country);

  const [serviceType, setServiceType] = useState<ComboboxItemPrice | null>(
    state?.serviceType ?? null
  );
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  const [mobileSearch, setMobileSearch] = useState(searchParams.get("q") ?? "");
  const [debouncedSearch] = useDebouncedValue(search?.toLocaleLowerCase(), 400);
  const [filteredDoctors, setFilteredDoctors] = useState<DoctorProfile[]>(data?.profilesData ?? []);
  const [opened, { open, close }] = useDisclosure(false);
  const [suggestionOpened, suggestionModal] = useDisclosure(false);
  const [suggestionSubmissionId, setSuggestionSubmissionId] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string>(searchParams.get("country") ?? "all"); 
  const [language, setLanguage] = useState("all");
  const [yearsOfExperience, setYearsOfExperience] = useState("all");
  const [modeOfService, setModeOfService] = useState("all");
  const [dateRange, setDateRange] = useState<string | null>("all-time");
  const [isNudgeDismissed, setIsNudgeDismissed] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);

  const locale = i18n.language === "fr" ? "fr" : "en";
  const countryOptions = getCountryValues(t, data?.countries ?? [], locale);
  const homepageHeading =
    locale === "fr"
      ? "Médecins en ligne en Afrique francophone"
      : "Online doctors in Francophone Africa";
  const specialtyLinks = specialtyLocationPages.slice(0, 6);

  // Sync search state with URL for persistence
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedCountry !== "all") {
      params.set("country", selectedCountry);
    } else {
      params.delete("country");
    }
    
    if (search) {
      params.set("q", search);
    } else {
      params.delete("q");
    }

    const nextSearch = params.toString();
    if (nextSearch === searchParams.toString()) {
      return;
    }
    
    setSearchParams(params, { replace: true, preventScrollReset: true });
  }, [selectedCountry, search]);

  useEffect(() => {
    setHasMounted(true);
    // Check if user has already dismissed the nudge in this session
    const dismissed = sessionStorage.getItem("laruche_nudge_dismissed");
    if (dismissed) setIsNudgeDismissed(true);
  }, []);

  const dismissNudge = () => {
    sessionStorage.setItem("laruche_nudge_dismissed", "true");
    setIsNudgeDismissed(true);
  };

  const openDoctorSuggestion = () => {
    setSuggestionSubmissionId(
      globalThis.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`
    );
    suggestionModal.open();
  };

  const isFirstRender = useRef(true);
  const doctorProfilesFetcher = useFetcher<OkDataError<DoctorProfile[]>>();
  const hasActiveDoctorFilters =
    !!debouncedSearch ||
    !!serviceType?.value ||
    language !== "all" ||
    yearsOfExperience !== "all" ||
    modeOfService !== "all" ||
    dateRange !== "all-time" ||
    (selectedCountry && selectedCountry !== "all");
  const profilesRequestSucceeded = hasActiveDoctorFilters
    ? doctorProfilesFetcher.data?.ok ?? ok.profiles
    : ok.profiles;

  useEffect(() => {
    if (doctorProfilesFetcher?.data?.data) {
      import.meta.env.DEV &&
        console.log("[DEV] doctor filter data:", doctorProfilesFetcher?.data);
      setFilteredDoctors(doctorProfilesFetcher.data.data);
    }
  }, [doctorProfilesFetcher?.data]);

  useEffect(() => {
    let queryString = "/api/filter-doctor-profiles?q=0"; // q=0 is just to help concatenate the other query params in a predictable way

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }

    // If there are no active filters, the route loader already has the right
    // doctor list. Avoid a second fetch and avoid replacing the rendered cards
    // with a spinner after hydration/revalidation.
    if (!hasActiveDoctorFilters && data?.profilesData) {
      setFilteredDoctors(data.profilesData);
      return;
    }

    // Include selected country for filtering (initially auto-detected, can be changed by user)
    if (selectedCountry && selectedCountry !== "all") {
      queryString += `&country=${selectedCountry}`;
    }

    if (debouncedSearch) {
      queryString += `&search=${debouncedSearch.trim()}`;
    }

    if (serviceType?.value) {
      queryString += `&serviceType=${
        serviceType.value === "all" ? "" : serviceType.value
      }`;
    }

    if (language && language !== "all") {
      queryString += `&language=${language}`;
    }

    if (yearsOfExperience && yearsOfExperience !== "all") {
      queryString += `&yearsOfExperience=${yearsOfExperience}`;
    }

    if (modeOfService && modeOfService !== "all") {
      queryString += `&modeOfService=${modeOfService}`;
    }

    if (dateRange && dateRange !== "all-time") {
      queryString += `&dateRange=${dateRange}`;
    }

    doctorProfilesFetcher.load(queryString);
    setFilteredDoctors([]); // Empty this first to prevent flash of previous doctors before the filtered ones are displayed
  }, [
    serviceType,
    debouncedSearch,
    data?.profilesData,
    language,
    yearsOfExperience,
    modeOfService,
    dateRange,
    selectedCountry, // Use selectedCountry (user's choice) not country (auto-detected)
    hasActiveDoctorFilters,
  ]);

  const actions: SpotlightActionData[] = useMemo(
    () => [
      {
        id: "doctors",
        group: "Doctors",
        actions: (data?.profilesData ?? []).map((doctor: DoctorProfile) => ({
          id: doctor?.slug,
          label: doctor?.name,
          // description: doctor?.specialization,
          onClick: () =>
            navigate({ pathname: `/${i18n.language}/doctor/${doctor?.slug}` }),
          leftSection: (
            <Image
              src={doctor?.image ?? placeholderPerson}
              alt={doctor?.name}
              h={30}
              w={30}
              width={30}
              height={30}
              radius={"100vmax"}
            />
          ),
        })),
      },
      {
        id: "specialties",
        group: "Specialties",
        actions: Array.from(
          new Set<string>(data?.specialtiesData ?? [])
        ).map(
            (specialty: string) => ({
              id: specialty,
              label: specialty,
              onClick: () => setSearch(specialty.toLocaleLowerCase()),
            })
          ),
      },
    ],

    [data?.profilesData, data?.specialtiesData]
  );

  return (
    <div className={classes.section}>
      {loaderData.structuredData ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(loaderData.structuredData),
          }}
        />
      ) : null}
      <div className={utilityClasses.container}>
        <form className={classes.body}>
          <BookHealthCareHero
            heading={homepageHeading}
            headingOrder={1}
          />

          <section className={classes["seo-intro"]}>
            <Text component="p" fz={{ base: 14, sm: 15 }} c="dimmed" ta="center">
              {t("homepage-seo-intro")}
            </Text>
            <BusinessReviewBadge mt="md" />
          </section>

          <div className={classes["flex-content"]}>
            <Paper withBorder p="1rem" className={classes["filters-container"]}>
              <Title order={2} fw={600} fz={18}>
                {t("filters")}
              </Title>

              {/* <Select
                label={t("service-type")}
                mt={"1rem"}
                data={data?.consultationServicesData?.map((item: any) => ({
                  value: item.value,
                  label: item.label,
                }))}
                value={serviceType?.value ?? null}
                onChange={(val, option) => {
                  setServiceType({
                    ...option,
                  });
                }}
                clearable
                clearButtonProps={{
                  onClick: () => setServiceType(null),
                }}
                placeholder={t("select-service")}
                searchable
              /> */}
              <Select
                label={t("availability")}
                placeholder=""
                mt="1rem"
                data={getDoctorAvailabilityValues(t)}
                value={dateRange}
                onChange={setDateRange}
              />
              <Select
                label={t("country")}
                placeholder={t("select-country")}
                mt="1rem"
                data={countryOptions}
                value={selectedCountry}
                onChange={(val) => setSelectedCountry(val || "all")}
              />
              <Stack gap={"7"} mt={"2rem"}>
                <Text component="p" fz={14} fw={500}>
                  {t("mode-of-service")}
                </Text>
                <Radio.Group value={modeOfService} onChange={setModeOfService}>
                  <Group gap={"xs"}>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"all"}
                      fz={13}>
                      {t("all")}
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"homecare"}
                      fz={13}>
                      {t("homecare")}
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"teleconsultation"}
                      fz={13}>
                      {t("teleconsultation")}
                    </RadioCard>
                  </Group>
                </Radio.Group>
              </Stack>

              <Stack gap={"7"} mt={"2rem"}>
                <Text component="p" fz={14} fw={500}>
                  {t("language")}
                </Text>
                <Radio.Group value={language} onChange={setLanguage}>
                  <Group gap={"xs"}>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"all"}
                      fz={13}>
                      {t("all")}
                    </RadioCard>
                    {data?.languages?.length
                      ? data.languages.map((language: string) => (
                          <RadioCard
                            key={language}
                            className={classes["radio-card"]}
                            w={"fit-content"}
                            radius={"xl"}
                            value={language.toLocaleLowerCase()}
                            fz={13}
                            tt="capitalize">
                            {t(language as TranslationKey)}
                          </RadioCard>
                        ))
                      : null}
                  </Group>
                </Radio.Group>
              </Stack>

              <Stack gap={"7"} mt={"2rem"}>
                <Text component="p" fz={14} fw={500}>
                  {t("years-of-experience")}
                </Text>
                <Radio.Group
                  value={yearsOfExperience}
                  onChange={setYearsOfExperience}>
                  <Group gap={"xs"}>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"all"}
                      fz={13}>
                      {t("all")}
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"2"}
                      fz={13}>
                      2+
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"5"}
                      fz={13}>
                      5+
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"10"}
                      fz={13}>
                      10+
                    </RadioCard>
                    <RadioCard
                      className={classes["radio-card"]}
                      w={"fit-content"}
                      radius={"xl"}
                      value={"15"}
                      fz={13}>
                      15+
                    </RadioCard>
                  </Group>
                </Radio.Group>
              </Stack>

              <Group mt={"2.5rem"} justify="space-between">
                <Button
                  variant="transparent"
                  size="compact-sm"
                  onClick={() => {
                    setServiceType(null);
                    setSelectedCountry(countryOptions.some(option => option.value === country) ? country : "all");
                    setLanguage("all");
                    setYearsOfExperience("all");
                    setModeOfService("all");
                    setDateRange("all-time");
                  }}>
                  {t("reset-filters")}
                </Button>
              </Group>
            </Paper>

            <div className={classes["cards-container"]}>
              <Group align="end" gap={7}>
                <TextInput
                  display={{ base: "none", sm: "block" }}
                  mt={{ base: "3rem", lg1: 0 }}
                  type="search"
                  placeholder={t("search-for-doctor-or-specialty")}
                  leftSection={<IconSearch size={18} />}
                  rightSection={
                    search !== "" ? (
                      <Input.ClearButton onClick={() => setSearch("")} />
                    ) : null
                  }
                  value={search}
                  onChange={val => setSearch(val.currentTarget.value as string)}
                  flex={"1 1"}
                  styles={{
                    input: {
                      paddingBlock: "1.5em",
                    },
                  }}
                />
                <InputBase
                  display={{ base: "block", sm: "none" }}
                  component="button"
                  type="button"
                  leftSection={<IconSearch size={18} />}
                  mt="3rem"
                  flex={"1 1"}
                  onClick={() => spotlight.open()}>
                  {search ? search : t("search")}
                </InputBase>

                <Spotlight
                  display={{ base: "block", sm: "none" }}
                  actions={actions}
                  nothingFound={
                    <Stack align="center" gap="xs" py="sm">
                      <Text size="sm" c="dimmed">
                        {t("nothing-found")}...
                      </Text>
                      {mobileSearch.trim() && (
                        <Button
                          type="button"
                          size="compact-sm"
                          variant="light"
                          onClick={() => {
                            setSearch(mobileSearch.trim());
                            spotlight.close();
                          }}>
                          {t("doctor-suggestion-search-platform")}
                        </Button>
                      )}
                    </Stack>
                  }
                  clearQueryOnClose={false}
                  highlightQuery
                  yOffset={30}
                  fz={14}
                  limit={20}
                  searchProps={{
                    leftSection: <IconSearch size={20} stroke={1.5} />,
                    placeholder: t("search-for-doctor-or-specialty"),
                    inputSize: "sm",
                    size: "md",
                    fz: 14,
                    type: "search",
                    onInput: e => setMobileSearch(e.currentTarget.value),
                    onKeyDown: e => {
                      if (e.key === "Enter") {
                        setSearch(e.currentTarget.value);
                        spotlight.close();
                      }
                    },
                  }}
                  scrollable
                  maxHeight={300}
                />

                <ActionIcon
                  variant="outline"
                  flex={"1 0"}
                  maw={30}
                  size={isSmall ? "xl" : "lg"}
                  display={{ base: "block", lg1: "none" }}
                  onClick={open}
                  title={t("filters")}>
                  <IconAdjustmentsHorizontal size={18} />
                </ActionIcon>

                <Drawer
                  display={{ base: "block", lg1: "none" }}
                  size={isSmall ? 400 : 600}
                  position={isSmall ? "right" : "bottom"}
                  radius={isSmall ? "15" : "15 15 0 0"}
                  offset={isSmall ? 8 : 0}
                  opened={isLarge1 ? false : opened}
                  onClose={close}
                  title={
                    <Text span fz={16} fw={500}>
                      {t("filters")}
                    </Text>
                  }
                  withCloseButton>
                  {/* <Select
                    label={t("service-type")}
                    data={data?.consultationServicesData?.map((item: any) => ({
                      value: item.value,
                      label: item.label,
                    }))}
                    value={serviceType?.value ?? null}
                    onChange={(_, option) => {
                      setServiceType({
                        ...option,
                      });
                    }}
                    clearable
                    clearButtonProps={{
                      onClick: () => setServiceType(null),
                    }}
                    placeholder={t("select-service")}
                    searchable
                  /> */}
                  <Select
                    label={t("availability")}
                    // mt="2rem"
                    data={getDoctorAvailabilityValues(t)}
                    value={dateRange}
                    onChange={setDateRange}
                  />
                  <Select
                    label={t("country")}
                    placeholder={t("select-country")}
                    mt="1rem"
                    data={countryOptions}
                    value={selectedCountry}
                    onChange={(val) => setSelectedCountry(val || "CI")}
                  />

                  <Stack gap={"7"} mt={"2rem"}>
                    <Text component="p" fz={14} fw={500}>
                      {t("mode-of-service")}
                    </Text>
                    <Radio.Group
                      value={modeOfService}
                      onChange={setModeOfService}>
                      <Group gap={"xs"}>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"all"}
                          fz={13}>
                          {t("all")}
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"homecare"}
                          fz={13}>
                          {t("homecare")}
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"teleconsultation"}
                          fz={13}>
                          {t("teleconsultation")}
                        </RadioCard>
                      </Group>
                    </Radio.Group>
                  </Stack>

                  <Stack gap={"7"} mt={"2rem"}>
                    <Text component="p" fz={14} fw={500}>
                      {t("language")}
                    </Text>
                    <Radio.Group value={language} onChange={setLanguage}>
                      <Group gap={"xs"}>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"all"}
                          fz={13}>
                          {t("all")}
                        </RadioCard>
                        {data?.languages?.length
                          ? data.languages.map((language: string) => (
                              <RadioCard
                                key={language}
                                className={classes["radio-card"]}
                                w={"fit-content"}
                                radius={"xl"}
                                value={language.toLocaleLowerCase()}
                                fz={13}
                                tt="capitalize">
                                {t(language as TranslationKey)}
                              </RadioCard>
                            ))
                          : null}
                      </Group>
                    </Radio.Group>
                  </Stack>

                  <Stack gap={"7"} mt={"2rem"}>
                    <Text component="p" fz={14} fw={500}>
                      {t("years-of-experience")}
                    </Text>
                    <Radio.Group
                      value={yearsOfExperience}
                      onChange={setYearsOfExperience}>
                      <Group gap={"xs"}>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"all"}
                          fz={13}>
                          {t("all")}
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"2"}
                          fz={13}>
                          2+
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"5"}
                          fz={13}>
                          5+
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"10"}
                          fz={13}>
                          10+
                        </RadioCard>
                        <RadioCard
                          className={classes["radio-card"]}
                          w={"fit-content"}
                          radius={"xl"}
                          value={"15"}
                          fz={13}>
                          15+
                        </RadioCard>
                      </Group>
                    </Radio.Group>
                  </Stack>

                  <Group
                    pos={"absolute"}
                    bottom={"1rem"}
                    right="1rem"
                    left="1rem"
                    mt={"auto"}
                    justify="space-between">
                    <Button
                      variant="transparent"
                      size="compact-sm"
                      onClick={() => {
                        setServiceType(null);
                        setSelectedCountry(countryOptions.some(option => option.value === country) ? country : "all");
                        setLanguage("all");
                        setYearsOfExperience("all");
                        setModeOfService("all");
                        setDateRange("all-time");
                      }}>
                      {t("reset-filters")}
                    </Button>
                    <Button variant="filled" onClick={close}>
                      {t("apply-filters")}
                    </Button>
                  </Group>
                </Drawer>
              </Group>

              {/* Country Auto-Detection Banner */}
              {hasMounted && selectedCountry === "all" && !isNudgeDismissed && (
                <Alert
                  variant="light"
                  color="blue"
                  radius="md"
                  mt="1rem"
                  withCloseButton
                  onClose={dismissNudge}
                  styles={{
                    root: {
                      backgroundColor: "rgba(59, 130, 246, 0.05)",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                    },
                  }}>
                  <Stack gap="sm">
                    <Text fz={14} c="blue.7">
                      {t("see-doctors-in-your-country")}
                    </Text>
                    <Group gap="xs" wrap="nowrap">
                      <Button
                        size="compact-sm"
                        variant="filled"
                        color="blue"
                        onClick={() => {
                          setSelectedCountry(countryOptions.some(option => option.value === country) ? country : "all");
                        }}>
                        {t("filter-by-x", {
                          country:
                            countryOptions.find(c => c.value === country)
                              ?.label || country,
                        })}
                      </Button>
                    </Group>
                  </Stack>
                </Alert>
              )}

              {/* Active Filter Header */}
              {selectedCountry !== "all" && (
                <Group justify="space-between" mt="1.5rem" align="center">
                  <Text fz={15} fw={500} c="dimmed">
                    {t("showing-specialists-in")}:{" "}
                    <Text component="span" c="blue.7" fw={700}>
                      {countryOptions.find(c => c.value === selectedCountry)?.label}
                    </Text>
                  </Text>
                  <Button
                    variant="transparent"
                    size="compact-xs"
                    c="gray.6"
                    onClick={() => setSelectedCountry("all")}
                    leftSection={<IconSearch size={14} />}>
                    {t("all-countries")}
                  </Button>
                </Group>
              )}

              <Group
                mt={selectedCountry === "all" ? "1.5rem" : "1rem"}
                gap={"md"}
                justify="space-between"
                align="start">
                {renderLoadingErrorData({
                  isLoading: doctorProfilesFetcher.state === "loading",
                  loadingComponent: (
                    <Center w="100%">
                      <Loader size={30} />
                    </Center>
                  ),
                  isError: !profilesRequestSucceeded,
                  errorComponent: (
                    <Alert
                      w={"100%"}
                      mt="1rem"
                      variant="light"
                      color={"red"}
                      radius={"sm"}
                      icon={<IconExclamationCircle />}
                      title={
                        <Text c={"red"} fw={500} fz={14}>
                          {t("oops")}
                        </Text>
                      }>
                      <Text fz={14} c={"red"}>
                        {t("doctors-error")}
                      </Text>
                    </Alert>
                  ),
                  isSuccess: profilesRequestSucceeded,
                  dataLength:
                    filteredDoctors?.length ??
                    doctorProfilesFetcher?.data?.data ??
                    0,
                  emptyDataComponent: (
                    debouncedSearch?.trim() && doctorSuggestionsEnabled ? (
                      <Paper
                        withBorder
                        radius="lg"
                        className={classes["doctor-suggestion-card"]}
                        w="100%">
                        <Stack align="flex-start" gap={0}>
                          <Title
                            order={3}
                            className={classes["doctor-suggestion-heading"]}>
                            {t("doctor-suggestion-empty-title")}
                          </Title>
                          <Text
                            className={classes["doctor-suggestion-description"]}>
                            {t("doctor-suggestion-empty-description")}
                          </Text>
                          <Button
                            type="button"
                            variant="outline"
                            color="blue"
                            className={classes["doctor-suggestion-button"]}
                            onClick={openDoctorSuggestion}>
                            {t("doctor-suggestion-open")}
                          </Button>
                        </Stack>
                      </Paper>
                    ) : (
                      <Text fz={15} c="dimmed" ta="center">
                        {t("empty-doctors")}
                      </Text>
                    )
                  ),
                  dataComponent: filteredDoctors?.map((doctor: DoctorProfile) => (
                    <DoctorCard
                      search={debouncedSearch ?? ""}
                      key={doctor.id}
                      doctor={doctor}
                      currency={currency}
                    />
                  )),
                })}
              </Group>
            </div>
          </div>

          <section className={classes["popular-specialties"]}>
            <Stack gap={6} align="center">
              <Title order={2} fw={600} fz={{ base: 20, sm: 24 }} ta="center">
                {t("homepage-popular-specialties-title")}
              </Title>
              <Text c="dimmed" fz={{ base: 14, sm: 15 }} ta="center">
                {t("homepage-popular-specialties-description")}
              </Text>
            </Stack>

            <Group justify="center" gap="xs" mt="lg">
              <Button
                component={Link}
                to={`/${locale}/specialites`}
                variant="light"
                color="primary"
                radius="xl"
                size="compact-md">
                {t("homepage-browse-specialties")}
              </Button>
              {specialtyLinks.map(page => (
                <Button
                  key={`${page.specialtySlug}-${page.citySlug}`}
                  component={Link}
                  to={`/${locale}/specialites/${page.specialtySlug}/${page.citySlug}`}
                  variant="subtle"
                  color="gray"
                  radius="xl"
                  size="compact-md">
                  {page.specialtyLabel[locale]}
                </Button>
              ))}
            </Group>
          </section>
        </form>
      </div>
      <DoctorSuggestionModal
        opened={suggestionOpened}
        onClose={suggestionModal.close}
        locale={locale}
        submissionId={suggestionSubmissionId}
        searchQuery={search.trim()}
        filters={{
          country: selectedCountry,
          service_type: serviceType?.value || "all",
          language,
          years_of_experience: yearsOfExperience,
          mode_of_service: modeOfService,
          date_range: dateRange || "all-time",
        }}
      />
    </div>
  );
}

export const loader: LoaderFunction = async ({ request, params }) => {
  // const t = await i18nextServer.getFixedT(request, "translation", {
  //   lng: "fr",
  // });
  const lang = params?.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const origin = new URL(request.url).origin;
  // console.log("book appointment locale:", locale);

  // Get country from Vercel geo-location header (ISO 3166-1 Alpha-2)
  const country = request?.headers?.get("x-vercel-ip-country") ?? "CI";
  console.log("User country detected:", country);

  const currencyContext = await resolveCurrencyContext(request, true);
  const currency = currencyContext?.currency ?? "XOF";
  const fxRate = currencyContext?.fxRate ?? 1;

  // * NB: Translation doesn't work for meta tags after deployment
  const normalizedLocale = locale === "fr" ? "fr" : "en";
  const title = homepageSeoCopy[normalizedLocale].title;
  const description = homepageSeoCopy[normalizedLocale].description;
  const keywords = homepageSeoCopy[normalizedLocale].keywords;
  const structuredData = getHomepageStructuredData({
    origin,
    locale: normalizedLocale,
  });
  const doctorSuggestionsEnabled =
    process.env.PUBLIC_DOCTOR_SUGGESTIONS_ENABLED === "true";

  try {
    const [data1, data2, filterOptions] = await Promise.all([
      getDoctorProfiles({}), // Fetch all doctors by default
      getConsultationServices(),
      getProfileFilterOptions(),
    ]);

    // Apply currency conversion to doctor service prices
    if (data1?.data) {
      data1.data = data1.data.map(doctor => ({
        ...doctor,
        services: doctor.services?.map(service => ({
          ...service,
          price: (service?.price ?? 0) * fxRate,
        })),
      }));
    }

    const data3 = {
      ok: true,
      error: null,
      data: data1?.data
        ? data1.data.reduce((specialties: string[], doctor: DoctorProfile) => {
            if (doctor?.specialties) {
              doctor.specialties.forEach(specialty =>
                specialties.push(specialty)
              );
            }
            return specialties;
          }, [] as string[])
        : [],
    };

    // Construct ok object to show status for each endpoint
    const ok = {
      profiles: data1.ok,
      consultationServices: data2.ok,
      specialties: data3.ok,
    };

    // Construct error object for each failed response
    const errors: Record<string, any> = {};
    if (!data1.ok) {
      errors.profilesError = data1?.error;
    }
    if (!data2.ok) {
      errors.consultationServicesError = data2?.error;
    }
    if (!data3.ok) {
      errors.specialtiesError = data3?.error;
    }

    // Extract languages from doctor profiles
    const languages = filterOptions.languages;

    // Unique languages
    const uniqueLanguages = Array.from(new Set(languages));

    // console.log("languages:", uniqueLanguages);

    // If any API call failed, return combined response with individual ok statuses
    if (Object.keys(errors).length > 0) {
      return json(
        {
          ok,
          title,
          description,
          keywords,
          structuredData,
          origin,
          locale,
          country, // Pass country to frontend
          currency, // Pass currency to frontend
          doctorSuggestionsEnabled,
          data: {
            profilesData: data1?.data || null,
            consultationServicesData: data2?.data || null,
            specialtiesData: data3?.data || null,
            languages: uniqueLanguages,
            countries: filterOptions.countries,
          },
          error: errors,
        },
        { status: 500 }
      );
    }

    // Return data if both requests succeeded
    return json(
      {
        ok,
        title,
        description,
        keywords,
        structuredData,
        origin,
        locale,
        country, // Pass country to frontend
        currency, // Pass currency to frontend
        doctorSuggestionsEnabled,
        data: {
          profilesData: data1.data,
          consultationServicesData: data2.data,
          specialtiesData: data3.data,
          languages: uniqueLanguages,
          countries: filterOptions.countries,
        },
        error: null,
      },
      {
        headers: {
          "Cache-Control": "max-age=300, stale-while-revalidate=300",
        },
      }
    );
  } catch (error) {
    // Handle any unexpected errors outside of API responses
    console.error("Unexpected error", error);
    return json(
      {
        ok: {
          profiles: false,
          consultationServices: false,
          specialties: false,
        },
        title,
        description,
        keywords,
        structuredData,
        locale,
        origin,
        country, // Pass country to frontend
        doctorSuggestionsEnabled,
        data: null,
        error: "Unexpected error occurred",
      },
      { status: 500 }
    );
  }
};
